import React, { useEffect, useMemo, useState } from "react";
import styles from "./ota-machine-header.module.scss";
import ConnectedMachineIndicator from "@src/components/atoms/indicator-text/icons/green-indicator-icon.svg";
import DisconnectedMachineIndicator from "@src/components/atoms/indicator-text/icons/orange-indicator-icon.svg";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { useI18N } from "@src/language/language-context";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "@src/reducers/store";
import {
  CONNECTED,
  FIRMWARE,
  ID,
  IP,
} from "@src/cloud-connect/ota-update/constants";
import SpinnerWithVariant from "@src/components/atoms/SpinnerWithVariant/spinner-with-variant";

// BEM block class name for SCSS module mapping
const cssClassName = "ota-machine-header";
interface OTAMachineHeaderProps {
  isLoading: boolean;
}

export const OTAMachineHeader = ({ isLoading }: OTAMachineHeaderProps) => {
  const i18n = useI18N(); // i18n translation hook

  const otaActiveView = useSelector(
    (state: RootState) => state.yourSlice.otaActiveView,
  );

  // Translated text labels
  const texts = useMemo(() => {
    return {
      currentFirmwareVersionText: i18n(
        "modules.otaMachineHeader.currentFirmwareVersionText",
      ),
      machineStatusAltText: i18n(
        "modules.otaMachineHeader.machineStatusAltText",
      ),
    };
  }, [i18n]);

  // Get machine source data from Redux store
  const source = useSelector(
    (state: RootState) => state.yourSlice?.connectionView?.source,
  );

  const connectionView = useSelector(
    (state: RootState) => state.yourSlice?.connectionView,
  );

  const isMachineConnected = source.connection_state === CONNECTED; // Machine connection status
  const firmwareVersion = connectionView?.source?.current_firmware_version;

  return (
    // Root container for OTA machine header
    <div className={styles[cssClassName]}>
      <FlexRowContainer justifyContent="space-between">
        {/* Machine status indicator and name */}
        <FlexRowContainer>
          <img
            src={
              isMachineConnected
                ? ConnectedMachineIndicator
                : DisconnectedMachineIndicator
            }
            alt={texts.machineStatusAltText} // Alt text for accessibility
          />
          <p className={styles[`${cssClassName}__title`]}>{source?.name}</p>
        </FlexRowContainer>

        {/* Display machine ID and IP */}
        <FlexRowContainer horizontalGap={5}>
          <div>
            <span className={styles[`${cssClassName}__badge-label`]}>{ID}</span>
            <span className={styles[`${cssClassName}__badge-value`]}>
              {source?.source_id}
            </span>
          </div>

          <div>
            <span className={styles[`${cssClassName}__badge-label`]}>{IP}</span>
            <span className={styles[`${cssClassName}__badge-value`]}>
              {source?.dsn}
            </span>
          </div>
        </FlexRowContainer>

        {/* Display current firmware version */}

        {
          <FlexRowContainer justifyContent="flex-end">
            {otaActiveView === FIRMWARE && firmwareVersion && (
              <>
                <div className={styles[`${cssClassName}__firmware-version`]}>
                  {texts.currentFirmwareVersionText}
                  {" : "}
                </div>
                {isLoading ? (
                  <div className={styles[`${cssClassName}__spinner-wrapper`]}>
                    <SpinnerWithVariant variant="small" />
                  </div>
                ) : (
                  <div className={styles[`${cssClassName}__firmware-version`]}>
                    {firmwareVersion}
                  </div>
                )}
              </>
            )}
          </FlexRowContainer>
        }
      </FlexRowContainer>
    </div>
  );
};
